// src/pages/Auth/Register.jsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTelegram } from '../../hooks/useTelegram';
import Button from '../../components/ui/Button';
import './Auth.css';

const Register = () => {
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register, loginWithGoogle, isAuthenticated, error: authError, user } = useAuth();
  const { webApp, isTelegramWebApp } = useTelegram();
  const navigate = useNavigate();

  // Rediriger si déjà authentifié avec un VRAI compte — pas un invité
  useEffect(() => {
    if (isAuthenticated && !user?.isGuest) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  // Mettre à jour l'erreur si provenant du contexte d'authentification
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // Gérer les changements de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation simple
    if (userData.password !== userData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await register(userData);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Inscription avec Google
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      await loginWithGoogle();
      navigate('/');
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'inscription avec Google');
    } finally {
      setLoading(false);
    }
  };

  // Inscription avec Telegram
  const handleTelegramRegister = () => {
    if (webApp) {
      // Logique spécifique à Telegram
      const telegramUser = webApp.initDataUnsafe?.user;
      if (telegramUser) {
        // Ici, vous devriez envoyer ces informations à votre backend
        console.log('Telegram user data for registration:', telegramUser);
      } else {
        setError('Impossible de récupérer les données utilisateur de Telegram');
      }
    } else {
      setError('WebApp Telegram non disponible');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Créer un compte</h1>
          <p>Rejoignez Elemental Duelists et commencez à jouer</p>
        </div>
        
        {error && <div className="auth-error">{error}</div>}
        
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Nom d'utilisateur</label>
            <input
              type="text"
              id="username"
              name="username"
              value={userData.username}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={userData.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <input
              type="password"
              id="password"
              name="password"
              value={userData.password}
              onChange={handleChange}
              required
              minLength="6"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={userData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="birthDate">Date de naissance</label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={userData.birthDate}
              onChange={handleChange}
              max={new Date().toISOString().slice(0, 10)}
            />
            <small className="auth-hint">
              Optionnelle. Requis pour les duels à mise (18+). Enregistrée une seule fois, non modifiable.
            </small>
          </div>
          
          <Button 
            type="primary"
            onClick={handleSubmit}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Inscription en cours...' : 'S\'inscrire'}
          </Button>
        </form>
        
        <div className="auth-social">
          <Button 
            type="outline"
            onClick={handleGoogleLogin}
            disabled={loading}
            fullWidth
            className="google-button"
          >
            <img src="/google-icon.png" alt="Google" className="google-icon" />
            S'inscrire avec Google
          </Button>
        </div>
        
        {isTelegramWebApp && (
          <Button 
            onClick={handleTelegramRegister}
            disabled={loading}
            className="telegram-auth-button"
          >
            S'inscrire avec Telegram
          </Button>
        )}
        
        <div className="auth-links">
          <p>
            Déjà un compte ?{' '}
            <Link to="/auth/login">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;